<template>
  <v-container style="height: 100%; width: 100%;">
    <v-sheet v-if="url" class="course-part-execution" color="blue" elevation="10">
      <div class="sync-status" :class="{'sync-status-warning': shouldBlockCourse}">
        <strong>Sync</strong>
        <span>{{ syncStatusText }}</span>
      </div>
      <div v-if="shouldBlockCourse" class="sync-overlay">
        <div class="sync-overlay-card">
          <strong>Fortschritt wird gerade nicht sicher gespeichert.</strong>
          <div>{{ syncProblemText }}</div>
        </div>
      </div>
      <iframe
          :src="url"
          width="100%"
          height="100%"
          @load="handleIframeLoad"
          allow="fullscreen"
          frameborder="0">
      </iframe>
      <v-snackbar :timeout="5000" :value="error" color="error" left shaped top app>
        Ein Fehler beim Speichern des Fortschrittes kam auf. Bitte Seite nicht weiter benutzen, bis der Sync wieder funktioniert.
      </v-snackbar>
      <v-snackbar :timeout="8000" :value="resumeStateRecoveryNotice" color="warning" left shaped top app>
        Der gespeicherte Kurszustand war fehlerhaft. Der Kursteil wurde ohne Resume-State geladen.
      </v-snackbar>
    </v-sheet>
  </v-container>
</template>

<script>
import "scorm-again"
import CompoundCoursePart from "@/models/CompoundCoursePart";

const SYNC_STALE_MS = 30000;
const SCORM_INIT_GRACE_MS = 60000;
const SYNC_FLUSH_INTERVAL_MS = 5000;
const SYNC_RETRY_BASE_MS = 5000;
const SYNC_RETRY_MAX_MS = 30000;

export default {
  name: "CoursePartExecution",

  data() {
    return {
      error: false,
      url: null,
      syncInterval: null,
      statusInterval: null,
      retryTimeout: null,
      syncInFlight: false,
      followUpSyncRequested: false,
      followUpSyncForce: false,
      syncDirty: false,
      syncVersion: 0,
      lastSyncAttemptAt: null,
      lastSyncSuccessAt: null,
      lastScormActivityAt: null,
      scormInitializedAt: null,
      playerLoadedAt: null,
      reportedSyncIssues: {},
      syncErrorCount: 0,
      nowTs: Date.now(),
      iframeLoadedAt: null,
      resumeStateDiagnostics: null,
      resumeStateStatus: "not_checked",
      resumeStateRecoveryNotice: false,
      syncRetryDelayMs: SYNC_RETRY_BASE_MS,
    }
  },
  props: {
    item: {
      type: CompoundCoursePart
    }
  },
  computed: {
    hasScormSignal() {
      return this.lastScormActivityAt !== null || this.scormInitializedAt !== null;
    },
    scormConnectionLate() {
      return this.playerLoadedAt !== null &&
          this.scormInitializedAt === null &&
          this.lastSyncSuccessAt === null &&
          (this.nowTs - this.playerLoadedAt) > SCORM_INIT_GRACE_MS;
    },
    syncIsStale() {
      if (this.syncInFlight || !this.hasScormSignal) {
        return false;
      }

      const referenceTime = this.lastSyncSuccessAt ?? this.scormInitializedAt ?? this.lastScormActivityAt;
      return referenceTime !== null && (this.nowTs - referenceTime) > SYNC_STALE_MS;
    },
    shouldBlockCourse() {
      return this.scormConnectionLate || this.syncIsStale;
    },
    syncStatusText() {
      if (this.syncInFlight) {
        return "Fortschritt wird gespeichert...";
      }
      if (this.lastSyncSuccessAt !== null) {
        return `letzte erfolgreiche Synchronisierung ${this.formatTimestamp(this.lastSyncSuccessAt)}`;
      }
      if (this.scormInitializedAt !== null) {
        return "SCORM verbunden, warte auf erste erfolgreiche Synchronisierung";
      }
      return "warte auf Verbindung mit dem Schulungspaket";
    },
    syncProblemText() {
      if (this.scormConnectionLate) {
        return "Das Schulungspaket hat keine stabile Verbindung zur LMS-Schnittstelle aufgebaut. Bitte Seite neu laden, bevor Sie fortfahren.";
      }
      if (this.lastSyncSuccessAt !== null) {
        return `Die letzte erfolgreiche Synchronisierung war am ${this.formatTimestamp(this.lastSyncSuccessAt)}. Bitte warten, bis die Verbindung wieder funktioniert.`;
      }
      return "Es gab noch keine bestätigte Synchronisierung. Bitte warten, bis der Fortschritt erfolgreich gespeichert wurde.";
    },
  },
  watch: {
    scormConnectionLate(value) {
      if (value) {
        this.reportSyncIssue("scorm_connection_late", "SCORM package did not initialize in time");
      }
    },
    syncIsStale(value) {
      if (value) {
        this.reportSyncIssue("sync_stale", "No confirmed part sync within 30 seconds");
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.syncInterval);
    clearInterval(this.statusInterval);
    clearTimeout(this.retryTimeout);
    window.removeEventListener("online", this.handleOnline);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.teardownScormApi();
  },
  created() {
    this.activeScormApi = null;
    this.activeScormStandard = null;
    this.scorm12Api = null;
    this.scorm2004Api = null;
    this.playerLoadedAt = Date.now();
    this.lastSyncSuccessAt = this.parseTimestamp(this.item?.courseRunPartDetails?.lastSyncDate);
    this.initializeScormApi();
  },
  mounted() {
    this.url = this.item.courseRunUrl;

    this.syncInterval = setInterval(() => {
      this.flushProgressSync("interval");
    }, SYNC_FLUSH_INTERVAL_MS);

    this.statusInterval = setInterval(() => {
      this.nowTs = Date.now();
    }, 1000);

    window.addEventListener("online", this.handleOnline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  methods: {
    initializeScormApi() {
      const settings = {dataCommitFormat: "json", autocommit: true, autocommitSeconds: 5, logLevel: 1};
      this.scorm12Api = new window.Scorm12API(settings);
      this.scorm2004Api = new window.Scorm2004API(settings);
      window.API = this.scorm12Api;
      window.API_1484_11 = this.scorm2004Api;
      this.loadPersistedState();

      this.attachScormListeners(this.scorm12Api, "1.2", {
        initialize: "LMSInitialize",
        setValue: "LMSSetValue",
        commit: "LMSCommit",
        terminate: "LMSFinish",
      });

      this.attachScormListeners(this.scorm2004Api, "2004", {
        initialize: "Initialize",
        setValue: "SetValue",
        commit: "Commit",
        terminate: "Terminate",
      });
    },
    attachScormListeners(api, standard, events) {
      api.on(events.initialize, () => {
        this.activateScormApi(api, standard);
        this.scormInitializedAt = Date.now();
        this.lastScormActivityAt = this.scormInitializedAt;
        this.markProgressDirty();
        this.flushProgressSync(`${standard}:initialize`, true);
      });

      api.on(`${events.setValue}.cmi.suspend_data`, () => {
        this.activateScormApi(api, standard);
        this.markProgressDirty();
      });

      api.on(`${events.setValue}.cmi.*`, () => {
        this.activateScormApi(api, standard);
        this.markProgressDirty();
      });

      api.on(events.commit, () => {
        this.activateScormApi(api, standard);
        this.markProgressDirty();
        this.flushProgressSync(`${standard}:commit`, true);
      });

      api.on(events.terminate, () => {
        this.activateScormApi(api, standard);
        this.markProgressDirty();
        this.flushProgressSync(`${standard}:terminate`, true);
      });
    },
    activateScormApi(api, standard) {
      this.activeScormApi = api;
      this.activeScormStandard = standard;
    },
    teardownScormApi() {
      this.clearScormApiListeners(this.scorm12Api, ["LMSInitialize", "LMSSetValue", "LMSCommit", "LMSFinish"]);
      this.clearScormApiListeners(this.scorm2004Api, ["Initialize", "SetValue", "Commit", "Terminate"]);

      if (window.API === this.scorm12Api) {
        window.API = null;
      }
      if (window.API_1484_11 === this.scorm2004Api) {
        window.API_1484_11 = null;
      }
    },
    clearScormApiListeners(api, events) {
      if (!api || !api.clear) {
        return;
      }
      events.forEach((event) => api.clear(event));
    },
    availableScormApis() {
      return [
        {api: this.scorm12Api, standard: "1.2"},
        {api: this.scorm2004Api, standard: "2004"},
      ].filter((entry) => entry.api);
    },
    parseTimestamp(value) {
      if (!value) {
        return null;
      }
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleString("de-DE");
    },
    toIsoString(timestamp) {
      return timestamp == null ? null : new Date(timestamp).toISOString();
    },
    reportSyncIssue(reason, detail) {
      const detailWithDiagnostics = this.withResumeDiagnostics(detail);
      const key = `${reason}:${detailWithDiagnostics || ""}`;
      if (this.reportedSyncIssues[key]) {
        return;
      }

      this.reportedSyncIssues[key] = true;
      this.$httpUserApi.reportCourseSyncIssue({
        courseRunId: this.item.courseRunId,
        courseId: null,
        partId: this.item.partId,
        reason: reason,
        detail: detailWithDiagnostics,
        lastSyncDate: this.toIsoString(this.lastSyncSuccessAt),
        lastScormActivityDate: this.toIsoString(this.lastScormActivityAt),
        scormInitializedDate: this.toIsoString(this.scormInitializedAt),
      }, null, () => {
      });
    },
    withResumeDiagnostics(detail) {
      const diagnostics = [
        detail,
        `resumeStateStatus=${this.resumeStateStatus}`,
      ];

      if (this.resumeStateDiagnostics) {
        diagnostics.push(`resumeStateLength=${this.resumeStateDiagnostics.length}`);
        diagnostics.push(`resumeStateHasCmi=${this.resumeStateDiagnostics.hasCmi}`);
        diagnostics.push(`resumeStateHasSuspendData=${this.resumeStateDiagnostics.hasSuspendData}`);
        if (this.resumeStateDiagnostics.completionStatus) {
          diagnostics.push(`completionStatus=${this.resumeStateDiagnostics.completionStatus}`);
        }
        if (this.resumeStateDiagnostics.successStatus) {
          diagnostics.push(`successStatus=${this.resumeStateDiagnostics.successStatus}`);
        }
        if (this.resumeStateDiagnostics.lessonStatus) {
          diagnostics.push(`lessonStatus=${this.resumeStateDiagnostics.lessonStatus}`);
        }
      }

      if (this.iframeLoadedAt !== null) {
        diagnostics.push(`iframeLoadedAt=${this.toIsoString(this.iframeLoadedAt)}`);
      }

      return diagnostics.filter((part) => part !== null && part !== undefined && part !== "").join(" | ");
    },
    collectResumeStateDiagnostics(rawState, parsedState) {
      const cmi = parsedState?.cmi ?? null;
      const suspendData = cmi?.suspend_data;
      return {
        length: rawState?.length ?? 0,
        hasCmi: cmi !== null,
        hasSuspendData: typeof suspendData === "string" ? suspendData.length > 0 : suspendData !== null && suspendData !== undefined,
        completionStatus: cmi?.completion_status ?? null,
        successStatus: cmi?.success_status ?? null,
        lessonStatus: cmi?.core?.lesson_status ?? null,
      };
    },
    triggerResumeStateRecoveryNotice() {
      this.resumeStateRecoveryNotice = true;
      setTimeout(() => {
        this.resumeStateRecoveryNotice = false;
      }, 8000);
    },
    loadPersistedState() {
      const rawState = this.item.state ?? null;
      if (!rawState || !rawState.includes("cmi")) {
        this.resumeStateStatus = "empty";
        this.resumeStateDiagnostics = this.collectResumeStateDiagnostics(rawState, null);
        return;
      }

      let parsedState;
      try {
        parsedState = JSON.parse(rawState);
      } catch (error) {
        this.resumeStateStatus = "invalid_json";
        this.resumeStateDiagnostics = this.collectResumeStateDiagnostics(rawState, null);
        this.triggerResumeStateRecoveryNotice();
        this.reportSyncIssue("resume_state_invalid", error?.message || "Persisted part resume state is not valid JSON");
        return;
      }

      this.resumeStateDiagnostics = this.collectResumeStateDiagnostics(rawState, parsedState);
      if (!parsedState?.cmi) {
        this.resumeStateStatus = "missing_cmi";
        this.triggerResumeStateRecoveryNotice();
        this.reportSyncIssue("resume_state_invalid", "Persisted part resume state does not contain cmi");
        return;
      }

      try {
        const loadErrors = [];
        let loaded = false;
        this.availableScormApis().forEach(({api, standard}) => {
          try {
            api.loadFromJSON(parsedState.cmi);
            loaded = true;
          } catch (error) {
            loadErrors.push(`${standard}: ${error?.message || "loadFromJSON failed"}`);
          }
        });

        if (loaded) {
          this.resumeStateStatus = "loaded";
        } else {
          this.resumeStateStatus = "load_failed";
          this.triggerResumeStateRecoveryNotice();
          this.reportSyncIssue("resume_state_load_failed", loadErrors.join("; ") || "loadFromJSON failed for part");
        }
      } catch (error) {
        this.resumeStateStatus = "load_failed";
        this.triggerResumeStateRecoveryNotice();
        this.reportSyncIssue("resume_state_load_failed", error?.message || "loadFromJSON failed for part");
      }
    },
    captureStateFromScorm() {
      try {
        const api = this.activeScormApi || this.scorm2004Api || this.scorm12Api;
        const state = api?.renderCMIToJSONString?.();
        if (state) {
          this.item.state = state;
          return state;
        }
      } catch (error) {
        console.error("Could not capture SCORM state for part", error);
        this.reportSyncIssue("state_capture_failed", error?.message || "renderCMIToJSONString failed");
      }
      return this.item.state;
    },
    markProgressDirty() {
      this.lastScormActivityAt = Date.now();
      this.syncDirty = true;
      this.syncVersion += 1;
    },
    clearRetryTimeout() {
      if (this.retryTimeout !== null) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
    },
    scheduleSyncRetry(reason) {
      if (this.retryTimeout !== null || !this.syncDirty) {
        return;
      }

      const delay = this.syncRetryDelayMs;
      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        this.flushProgressSync(`${reason}:retry`, true);
      }, delay);
      this.syncRetryDelayMs = Math.min(this.syncRetryDelayMs * 2, SYNC_RETRY_MAX_MS);
    },
    flushProgressSync(reason, force = false) {
      if (!force && !this.syncDirty) {
        return;
      }

      if (this.syncInFlight) {
        this.followUpSyncRequested = true;
        if (force) {
          this.followUpSyncForce = true;
        }
        return;
      }

      const state = this.captureStateFromScorm();
      if (!state) {
        this.reportSyncIssue("empty_scorm_state", `No state available during ${reason}`);
        return;
      }

      this.lastSyncAttemptAt = Date.now();
      const syncedVersion = this.syncVersion;
      this.syncInFlight = true;
      this.clearRetryTimeout();
      this.$httpUserApi.trackProgressForPart(
          this.item,
          () => {
            this.lastSyncSuccessAt = Date.now();
            this.syncErrorCount = 0;
            this.syncRetryDelayMs = SYNC_RETRY_BASE_MS;
            this.error = false;
            this.syncInFlight = false;
            if (this.syncVersion === syncedVersion) {
              this.syncDirty = false;
            }
            const shouldFlushAgain = this.followUpSyncRequested || this.syncDirty;
            const shouldForceFollowUp = this.followUpSyncForce;
            this.followUpSyncRequested = false;
            this.followUpSyncForce = false;
            if (shouldFlushAgain && shouldForceFollowUp) {
              this.flushProgressSync(`${reason}:follow_up`, true);
            }
          },
          (err) => {
            this.syncErrorCount += 1;
            this.syncInFlight = false;
            this.error = true;
            console.error(`Progress sync failed for part (${reason})`, err);
            const status = err?.response?.status ?? "network";
            this.reportSyncIssue("progress_request_failed", `Part progress sync failed during ${reason} (status=${status})`);
            this.scheduleSyncRetry(reason);
          }
      );
    },
    handleOnline() {
      this.nowTs = Date.now();
      this.flushProgressSync("online", true);
    },
    handleVisibilityChange() {
      this.nowTs = Date.now();
      if (document.visibilityState === "visible") {
        this.flushProgressSync("visible", true);
      }
    },
    handleIframeLoad() {
      this.iframeLoadedAt = Date.now();
    },
  }
}
</script>

<style scoped>
.course-part-execution {
  position: relative;
  width: 100%;
  height: 100vh;
  padding-left: 0;
  padding-right: 0;
}

.sync-status {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  max-width: min(420px, calc(100% - 24px));
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
  color: #16324f;
  font-size: 13px;
  line-height: 1.35;
  text-align: right;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  pointer-events: none;
  backdrop-filter: blur(2px);
}

.sync-status strong {
  font-size: 11px;
}

.sync-status-warning {
  background: rgba(120, 18, 18, 0.74);
  color: #fff;
}

.sync-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.44);
  padding: 24px;
  pointer-events: none;
}

.sync-overlay-card {
  max-width: 520px;
  padding: 18px 20px;
  border-radius: 12px;
  background: rgba(255, 244, 232, 0.9);
  color: #5c2b00;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
}

.sync-overlay-card strong {
  display: block;
  margin-bottom: 8px;
}
</style>
